import { useEffect } from "react";
import * as yup from "yup";
import { FormProvider, useForm, SubmitHandler, DefaultValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { nameof } from "ts-simple-nameof";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { DefaultInputField } from "../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { DefaultTextareaField } from "../../../../common/components/forms/inputs/textareaField/DefaultTextareaField";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { useFormValidationMessages } from "../../../../common/infrastructure/hooks/useFormHelper";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { useAppContext } from "../../../../AppContext";
import styles from "./InventoryAdjustQuantityDialog.module.scss";

export interface IAdjustQuantityFormModel {
  itemId: number;
  newQuantity: number;
  reason: string;
}

const schema = yup
  .object({
    itemId: yup.number().required(),
    newQuantity: yup
      .number()
      .typeError("Новото количество е задължително")
      .min(0, "Количеството не може да е отрицателно")
      .required("Новото количество е задължително")
      .defined(),
    reason: yup
      .string()
      .required("Причината е задължителна")
      .min(2, "Причината трябва да е поне 2 символа")
      .max(500, "Причината не може да е по-дълга от 500 символа")
      .defined(),
  })
  .required();

interface IInventoryAdjustQuantityDialogProps {
  visible: boolean;
  onHide: () => void;
  itemId: number;
  itemName: string;
  currentQuantity: number;
}

export const InventoryAdjustQuantityDialog = ({
  visible,
  onHide,
  itemId,
  itemName,
  currentQuantity,
}: IInventoryAdjustQuantityDialogProps) => {
  const { selectedStudioId } = useAppContext();

  const defaults: DefaultValues<IAdjustQuantityFormModel> = {
    itemId,
    newQuantity: currentQuantity,
    reason: "",
  };

  const form = useForm<IAdjustQuantityFormModel>({
    resolver: yupResolver(schema) as any,
    defaultValues: defaults,
    mode: "onSubmit",
  });

  useFormValidationMessages<IAdjustQuantityFormModel>(form);

  useEffect(() => {
    if (visible) {
      form.reset({ itemId, newQuantity: currentQuantity, reason: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, itemId, currentQuantity]);

  const { mutate, isLoading } = useApiMutation({
    onSuccess: () => {
      onHide();
    },
  });

  const handleSubmit: SubmitHandler<IAdjustQuantityFormModel> = (data) => {
    mutate({
      endpoint: endpointsMetadata.inventoryItemNomenclature.adjustQuantity,
      method: "put",
      payload: { ...data, reason: data.reason.trim() },
      invalidateKeys: [
        [endpointsMetadata.inventoryItemNomenclature.getAllCategorized, selectedStudioId],
        [endpointsMetadata.inventoryItemNomenclature.getAll, selectedStudioId],
        [endpointsMetadata.inventoryItemNomenclature.getAllWithDeleted, selectedStudioId],
        [endpointsMetadata.inventoryAuditLog.getItemHistory, itemId],
      ],
    });
  };

  return (
    <AppDialog visible={visible} header={`Корекция на наличност: ${itemName}`} onHide={onHide}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className={styles.form}>
          <div className={styles.current}>Текуща наличност: <strong>{currentQuantity}</strong></div>
          <DefaultInputField
            name={nameof<IAdjustQuantityFormModel>((x) => x.newQuantity)}
            label="Нова наличност"
            placeholder="0"
            type="number"
            min={0}
            step={0.001}
            width="100%"
            required
          />
          <DefaultTextareaField
            name={nameof<IAdjustQuantityFormModel>((x) => x.reason)}
            label="Причина за корекцията"
            placeholder="напр. инвентаризация, повредена стока..."
            rows={3}
            required
          />
          <div className={styles.actions}>
            <DefaultButton
              label="Отказ"
              design="outline"
              onClick={onHide}
              type="button"
              width="120px"
            />
            <DefaultButton
              label="Запази"
              type="submit"
              loading={isLoading}
              width="120px"
            />
          </div>
        </form>
      </FormProvider>
    </AppDialog>
  );
};
